/**
 * Storage Quota Service
 * Provides client-side integration with storage quota Cloud Functions
 */

import { getFunc } from './FireClient';

export interface QuotaValidationResult {
    allowed: boolean;
    reason?: 'WORKSPACE_QUOTA_EXCEEDED' | 'PERSONAL_QUOTA_EXCEEDED';
    current: number;
    max: number;
}

export interface AllocationResult {
    success: boolean;
    allocatedBytes: number;
}

/**
 * Validate if a file upload is allowed based on quota
 * @param fileSize - Size of the file in bytes
 * @param workspaceId - Workspace ID (for workspace context uploads)
 * @param context - 'workspace' for room uploads, 'personal' for my room/1:1
 */
export async function validateStorageQuota(
    fileSize: number,
    workspaceId?: string,
    context: 'workspace' | 'personal' = 'workspace'
): Promise<QuotaValidationResult> {
    try {
        const func = getFunc('validateStorageQuota');
        const result = await func({ fileSize, workspaceId, context });
        return result.data as QuotaValidationResult;
    } catch (error) {
        console.error('validateStorageQuota error:', error);
        // Default to allowed on error to not block user
        return { allowed: true, current: 0, max: 0 };
    }
}

/**
 * Validate multiple files total size
 * @param files - Array of files to check
 * @param workspaceId - Workspace ID
 * @param context - Upload context
 */
export async function validateFilesQuota(
    files: File[],
    workspaceId?: string,
    context: 'workspace' | 'personal' = 'workspace'
): Promise<QuotaValidationResult> {
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    return validateStorageQuota(totalSize, workspaceId, context);
}

/**
 * Update storage allocation for a user (Admin only)
 * @param workspaceId - Workspace ID
 * @param targetUserId - User to allocate to
 * @param newBytes - New allocation in bytes
 */
export async function updateAllocation(
    workspaceId: string,
    targetUserId: string,
    newBytes: number
): Promise<AllocationResult> {
    const func = getFunc('updateAllocation');
    const result = await func({ workspaceId, targetUserId, newBytes });
    return result.data as AllocationResult;
}

/**
 * Recalculate user quota (when memberships change)
 * @param targetUserId - User ID to recalculate
 */
export async function recalculateUserQuota(targetUserId: string): Promise<{ success: boolean }> {
    const func = getFunc('recalculateUserQuota');
    const result = await func({ targetUserId });
    return result.data as { success: boolean };
}

// Helper: Format bytes to human readable
export function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Types for stats responses
export interface MemberStorageStat {
    userId: string;
    name: string;
    email: string;
    role: string;
    allocatedBytes: number;
    usedBytes: number;
}

export interface RoomStorageStat {
    roomId: string;
    name: string;
    usedBytes: number;
    fileCount: number;
}

export interface MyStorageStats {
    totalUsed: number;
    maxBytes: number;
    baseBytes: number;
    fileCount: number;
    allocations: Array<{
        workspaceId: string;
        workspaceName: string;
        allocatedBytes: number;
    }>;
    roomUsage: Array<{
        roomId: string;
        roomName: string;
        workspaceId: string | null;
        usedBytes: number;
        fileCount: number;
    }>;
}

/**
 * Get member storage stats for a workspace (Admin only)
 * @param workspaceId - Workspace ID
 */
export async function getMemberStorageStats(workspaceId: string): Promise<{ members: MemberStorageStat[] }> {
    const func = getFunc('getMemberStorageStats');
    const result = await func({ workspaceId });
    return result.data as { members: MemberStorageStat[] };
}

/**
 * Get room storage stats for a workspace (Admin only)
 * @param workspaceId - Workspace ID
 */
export async function getRoomStorageStats(workspaceId: string): Promise<{ rooms: RoomStorageStat[] }> {
    const func = getFunc('getRoomStorageStats');
    const result = await func({ workspaceId });
    return result.data as { rooms: RoomStorageStat[] };
}

/**
 * Get current user's storage stats with room breakdown
 */
export async function getMyStorageStats(): Promise<MyStorageStats> {
    const func = getFunc('getMyStorageStats');
    const result = await func({});
    return result.data as MyStorageStats;
}
